import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PrevisionItem } from './useSavePlanning';

const buildPrevisionsFromData = (
	planningData: any[],
	cycle: any
): PrevisionItem[] => {
	if (!cycle) return [];

	if (!planningData || planningData.length === 0) {
		return Array.from({ length: cycle.duracao }, (_, index) => {
			const date = new Date(cycle.ano, cycle.ciclo + index, 1);
			return {
				monthIndex: date.getMonth(),
				year: date.getFullYear(),
				quantity: 0,
			};
		});
	}

	const previsionByMonth: Record<string, number> = {};

	if (planningData && planningData.length > 0) {
		planningData.forEach(p => {
			const [year, month] = p.month_year.split('-');
			const date = new Date(+year, +month - 1, 1);
			const monthIndex = date.getMonth();
			const yearValue = date.getFullYear();
			const key = `${yearValue}-${monthIndex}`;
			previsionByMonth[key] = +p.quantity || 0;
		});
	}

	return Array.from({ length: cycle.duracao }, (_, index) => {
		const date = new Date(cycle.ano, cycle.ciclo + index, 1);
		const monthIndex = date.getMonth();
		const yearValue = date.getFullYear();
		const key = `${yearValue}-${monthIndex}`;
		const quantity = previsionByMonth[key] || 0;
		return {
			monthIndex,
			year: yearValue,
			quantity,
		};
	});
};

const calculatePrevisionTotals = (previsions: PrevisionItem[]) => {
	if (!previsions.length) return { total: 0, average: 0 };

	const total = previsions.reduce((sum, prev) => sum + prev.quantity, 0);
	const average = total / previsions.length;

	return { total, average };
};

interface IUsePlanningPrevisionsParams {
	planningData: any[];
	cycle: any;
	manualFillType: string;
	dataUpdatedAt?: number;
}

export const usePlanningPrevisions = ({
	planningData,
	cycle,
	manualFillType,
	dataUpdatedAt,
}: IUsePlanningPrevisionsParams) => {
	const [previsions, setPrevisions] = useState<PrevisionItem[]>([]);
	const [previsionsDraft, setPrevisionsDraft] = useState<PrevisionItem[]>([]);
	const [originalPrevisions, setOriginalPrevisions] = useState<
		PrevisionItem[]
	>([]);
	const [manualPrevisionInput, setManualPrevisionInput] = useState<
		PrevisionItem[]
	>([]);
	const lastPlanningDataRef = useRef<any[]>();
	const previsionsDraftRef = useRef<PrevisionItem[]>([]);

	const newPrevisions = useMemo(() => {
		if (planningData && cycle) {
			return buildPrevisionsFromData(planningData, cycle);
		}
		return [];
	}, [planningData, cycle]);

	useEffect(() => {
		previsionsDraftRef.current = previsionsDraft;
	}, [previsionsDraft]);

	useEffect(() => {
		const planningDataChanged =
			!lastPlanningDataRef.current ||
			!isEqual(lastPlanningDataRef.current, planningData);
		const hasChanged = !isEqual(originalPrevisions, newPrevisions);

		if (hasChanged || planningDataChanged || dataUpdatedAt) {
			lastPlanningDataRef.current = planningData;

			setPrevisions(newPrevisions);
			setOriginalPrevisions(newPrevisions);
			setManualPrevisionInput(newPrevisions);

			const isEmptyResponse =
				newPrevisions.length === 0 ||
				newPrevisions.every(item => item.quantity === 0);
			const draftHasData = previsionsDraftRef.current.some(
				item => item.quantity !== 0
			);
			const shouldUpdateDraft =
				manualFillType === 'Previsão Manual' ||
				(isEmptyResponse && !draftHasData);

			if (shouldUpdateDraft) {
				setPrevisionsDraft(newPrevisions);
			}
		}
	}, [
		newPrevisions,
		originalPrevisions,
		manualFillType,
		planningData,
		dataUpdatedAt,
	]);

	const resetPrevisionsDraft = useCallback(() => {
		setPrevisionsDraft(originalPrevisions);
	}, [originalPrevisions]);

	const { total, average } = calculatePrevisionTotals(previsionsDraft);

	return {
		previsions,
		previsionsDraft,
		originalPrevisions,
		total,
		average,
		setPrevisionsDraft,
		resetPrevisionsDraft,
		manualPrevisionInput,
		setManualPrevisionInput,
	};
};
